import { ArrowRight, Check } from "lucide-react";
import { useSEO } from "../lib/seo";
import { useCmsSeo } from "../lib/cmsSeo";
import { usePageCms } from "../cms/usePageCms";
import { SOLUTIONS_TIERS } from "../data/content";

export default function SolutionsPage({ onBook }) {
  const seo = useCmsSeo("solutions", {
    title: "Solutions — Retro, Smart Touch & KNX",
    description:
      "Three tiers built for every Indian home. Retro modules for existing homes, Smart Touch panels for new builds, and KNX Wired for luxury villas.",
    path: "/solutions",
    keywords: "smart home solutions India, KNX automation, retro module, smart touch panels",
  });
  useSEO(seo);
  const { data: pageCms } = usePageCms("solutions");
  const hero = pageCms.hero || {};

  return (
    <main data-testid="solutions-page" className="bg-[var(--gx-bg)] text-white">
      {hero.visible !== false && (
      <section className="relative pt-32 lg:pt-40 pb-16 lg:pb-20 grain">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(201,169,97,0.12),transparent_60%)]" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10">
          <p className="eyebrow mb-4">{hero.eyebrow || "Solutions"}</p>
          <h1 className="h1-display max-w-[18ch]">
            {hero.heading_line1 || "Three ways to bring"} <span className="text-gold">{hero.heading_accent || "intelligence home"}</span>.
          </h1>
          <p className="lead mt-6 max-w-[56ch]">
            {hero.description ||
              "Whether you live in an existing apartment, are designing a new build, or commissioning a villa — there is a GenieX system shaped for you."}
          </p>
          {(hero.primary_cta_label || hero.secondary_cta_label) && (
            <div className="flex flex-wrap gap-3 mt-8">
              {hero.primary_cta_label && (
                <a href={hero.primary_cta_link || "#"} className="btn-gold">
                  {hero.primary_cta_label} <ArrowRight className="w-4 h-4" />
                </a>
              )}
              {hero.secondary_cta_label && (
                <a href={hero.secondary_cta_link || "#"} className="btn-outline">
                  {hero.secondary_cta_label}
                </a>
              )}
            </div>
          )}
        </div>
      </section>
      )}
      <section className="border-t border-line">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-3 gap-6 lg:gap-8">
          {SOLUTIONS_TIERS.map((t) => (
            <div
              key={t.key}
              data-testid={`solution-tier-${t.key}`}
              className={`relative rounded-3xl border ${t.featured ? "border-gold/60 bg-gradient-to-b from-amber-200/8 to-transparent" : "border-line bg-[var(--gx-surface)]"} p-8 flex flex-col`}
            >
              {t.featured && (
                <span className="absolute -top-3 left-6 text-[10px] tracking-[0.28em] uppercase bg-gold text-[#0a0a0c] px-3 py-1 rounded-full">
                  Most Loved
                </span>
              )}
              <p className="eyebrow mb-3 text-gold">{t.badge}</p>
              <h2 className="font-display text-3xl">{t.title}</h2>
              <p className="text-dim text-sm mt-1">{t.sub}</p>
              <p className="font-display text-xl mt-6 text-white">{t.price}</p>
              <p className="text-dim text-[14px] leading-relaxed mt-4 flex-1">{t.body}</p>
              <button onClick={onBook} className="btn-outline mt-7 w-full justify-center">
                Discuss this option <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-line bg-[var(--gx-black)]">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16 lg:py-24">
          <h2 className="h2-display max-w-[22ch]">Every system includes.</h2>
          <ul className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              "5-Year Free Replacement Warranty",
              "Certified Professional Installation",
              "Remote Support, 7 Days a Week",
              "Personalised Scene Programming",
              "Mobile App Control (iOS & Android)",
              "Alexa & Google Home Integration",
            ].map((f) => (
              <li key={f} className="flex items-start gap-3 text-[14px] text-dim">
                <span className="w-7 h-7 rounded-full border border-gold/40 bg-gold/10 flex items-center justify-center shrink-0">
                  <Check className="w-3.5 h-3.5 text-gold" />
                </span>
                {f}
              </li>
            ))}
          </ul>
        </div>
      </section>
    </main>
  );
}
